import AboutUsImage from '@/assets/images/about-us-team.webp';
import BoostHomeImage from '@/assets/images/boost-banner.webp';
import BoostStoreImage from '@/assets/images/boost-store-banner.webp';
import BusinessBoostImage from '@/assets/images/business/boost-product-image.webp';
import BusinessHomeImage from '@/assets/images/business/hero-image.jpg';
import BusinessLaybyImage from '@/assets/images/business/layby-product-image.webp';
import BusinessEnquiryImage from '@/assets/images/business/layup-merchant-enquiry-banner.webp';
import MerchantPaymentPlantImage from '@/assets/images/business/merchant-payment-plan.webp';
import PricingImage from '@/assets/images/card-machine.webp';
import LaybyStoreImage from '@/assets/images/layby-store-banner.webp';
import LaybyHomeImage from '@/assets/images/layup-banner.webp';
import Logo from '@/assets/images/logo.webp';
import PlansHomeImage from '@/assets/images/plans-banner.webp';
import PlansStoreImage from '@/assets/images/plans-store-banner.webp';
import { BASE_URL, isProduction } from '@/config';
import seoMetaData from '@/data/seoMetaData.json';
import BusinessLayout from '@/layouts/BusinessLayout.vue';
import MainLayout from '@/layouts/MainLayout.vue';
import { useThemeStore } from '@/stores/theme';
import EnquiryPage from '@/views/business/EnquiryPage.vue';
import LaybyProductPage from '@/views/business/LaybyProductPage.vue';
import HomePage from '@/views/HomePage.vue';
import { useSeoMeta } from '@unhead/vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import MerchantPaymentPlanPage from '../views/business/MerchantPaymentPlanProductPage.vue';
import SaveNowBuyLaterPage from '../views/business/SaveNowBuyLaterProductPage.vue';

/**
 * SHOPPER ROUTES
 * ==========================
 * Boost home page
 * Layby home page
 * Store directory
 * Shopper help
 * Shopper contact
 * Blog
 *
 *
 * MERCHANT ROUTES
 * ==========================
 * Merchant homepage
 * Products [Layby/boost subscription]
 * Integrations
 * resources
 * Blogs
 * Careers
 * About us
 * Pricing
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: MainLayout,
    redirect: '/layby',
    children: [
      {
        path: 'layby',
        name: 'layby-home',
        component: HomePage,
        meta: {
          title: seoMetaData.meta.laybyHome.title,
          description: seoMetaData.meta.laybyHome.description,
          image: LaybyHomeImage,
          product: 'layby',
        },
      },
      {
        path: 'layby/stores',
        name: 'layby-stores',
        component: () => import('@/views/ShopPage.vue'),
        meta: {
          title: seoMetaData.meta.laybyStores.title,
          description: seoMetaData.meta.laybyStores.description,
          image: LaybyStoreImage,
          showSocials: true,
          product: 'layby',
        },
      },
      {
        path: 'contact-us',
        name: 'shopper-contact-us',
        component: () => import('@/views/ContactPage.vue'),
        meta: {
          title: seoMetaData.meta.shopperContactUs.title,
          description: seoMetaData.meta.shopperContactUs.description,
          showSocials: false,
        },
      },
      ...(isProduction
        ? []
        : ([
            {
              path: 'plans',
              name: 'plans-home',
              component: HomePage,
              meta: {
                title: seoMetaData.meta.plansHome.title,
                description: seoMetaData.meta.plansHome.description,
                image: PlansHomeImage,
                product: 'plans',
              },
            },
            {
              path: 'plans/stores',
              name: 'plans-stores',
              component: () => import('@/views/ShopPage.vue'),
              meta: {
                title: seoMetaData.meta.plansStores.title,
                description: seoMetaData.meta.plansStores.description,
                image: PlansStoreImage,
                showSocials: true,
                product: 'plans',
              },
            },
          ] as RouteRecordRaw[])),
      {
        path: 'boost',
        name: 'boost-home',
        component: HomePage,
        meta: {
          title: seoMetaData.meta.boostHome.title,
          description: seoMetaData.meta.boostHome.description,
          image: BoostHomeImage,
          product: 'boost',
        },
      },
      {
        path: 'boost/stores',
        name: 'boost-stores',
        component: () => import('@/views/ShopPage.vue'),
        meta: {
          title: seoMetaData.meta.boostStores.title,
          description: seoMetaData.meta.boostStores.description,
          image: BoostStoreImage,
          showSocials: true,
          product: 'boost',
        },
      },
      {
        path: 'help-center',
        name: 'help-center',
        component: () => import('@/views/HelpCenterPage.vue'),
        meta: {
          title: seoMetaData.meta.helpCenter.title,
          description: seoMetaData.meta.helpCenter.description,
        },
      },
      {
        path: 'blog',
        name: 'shopper-blog',
        component: () => import('@/views/shopper/Blog/BlogPost.vue'),
        meta: {
          title: seoMetaData.meta.shopperBlog.title,
          description: seoMetaData.meta.shopperBlog.description,
        },
      },
      {
        path: 'blog/:id',
        name: 'shopper-blog-post',
        component: () => import('@/views/shopper/Blog/BlogPostDetail.vue'),
        meta: {
          title: 'Blog - LayUp Technologies',
        },
      },
    ],
  },
  {
    path: '/business',
    component: BusinessLayout,
    children: [
      {
        path: '',
        name: 'business-home',
        component: () => import('@/views/BusinessHomePage.vue'),
        meta: {
          title: seoMetaData.meta.businessHome.title,
          description: seoMetaData.meta.businessHome.description,
          image: BusinessHomeImage,
          mode: 'business',
        },
      },
      {
        path: 'layby/pricing',
        name: 'business-pricing-layby',
        component: () => import('@/views/BusinessPricingPage.vue'),
        meta: {
          title: seoMetaData.meta.laybyPricing.title,
          description: seoMetaData.meta.laybyPricing.description,
          image: PricingImage,
          mode: 'business',
          product: 'layby',
        },
      },
      {
        path: 'boost/pricing',
        name: 'business-pricing-boost',
        component: () => import('@/views/BusinessPricingPage.vue'),
        meta: {
          title: seoMetaData.meta.boostPricing.title,
          description: seoMetaData.meta.boostPricing.description,
          image: PricingImage,
          mode: 'business',
          product: 'boost',
        },
      },
      {
        path: 'pricing',
        redirect: '/business/layby/pricing',
      },
      {
        path: 'enquiry',
        name: 'business-enquiry',
        component: EnquiryPage,
        meta: {
          title: seoMetaData.meta.businessEnquiry.title,
          description: seoMetaData.meta.businessEnquiry.description,
          image: BusinessEnquiryImage,
          mode: 'business',
        },
      },
      {
        path: 'layby',
        name: 'layby-product',
        component: LaybyProductPage,
        meta: {
          title: seoMetaData.meta.businessLayby.title,
          description: seoMetaData.meta.businessLayby.description,
          image: BusinessLaybyImage,
          mode: 'business',
        },
      },
      {
        path: 'save-now-buy-later',
        name: 'save-now-buy-later-product',
        component: SaveNowBuyLaterPage,
        meta: {
          title: seoMetaData.meta.businessBoost.title,
          description: seoMetaData.meta.businessBoost.description,
          image: BusinessBoostImage,
          mode: 'business',
        },
      },
      {
        path: 'merchant-payment-plan',
        name: 'merchant-payment-plan-product',
        component: MerchantPaymentPlanPage,
        meta: {
          title: seoMetaData.meta.businessMerchantPaymentPlan.title,
          description: seoMetaData.meta.businessMerchantPaymentPlan.description,
          image: MerchantPaymentPlantImage,
          mode: 'business',
        },
      },
      {
        path: 'about-us',
        name: 'about-us',
        component: () => import('@/views/business/AboutUsPage.vue'),
        meta: {
          title: seoMetaData.meta.aboutUs.title,
          description: seoMetaData.meta.aboutUs.description,
          image: AboutUsImage,
          mode: 'business',
        },
      },
      {
        path: 'faq',
        name: 'business-faq',
        component: () => import('@/views/FaqBusinessPage.vue'),
        meta: {
          title: seoMetaData.meta.businessFaq.title,
          description: seoMetaData.meta.businessFaq.description,
          mode: 'business',
        },
      },
      {
        path: 'blog',
        name: 'business-blog',
        component: () => import('@/views/business/blog/BusinessBlogPost.vue'),
        meta: {
          title: seoMetaData.meta.businessBlog.title,
          description: seoMetaData.meta.businessBlog.description,
          mode: 'business',
        },
      },
      {
        path: 'blog/:id',
        name: 'business-blog-post',
        component: () => import('@/views/business/blog/BusinessBlogPostDetail.vue'),
        meta: {
          title: 'Business Blog - LayUp Technologies',
          mode: 'business',
        },
      },
      {
        path: 'careers',
        name: 'business-careers',
        component: () => import('@/views/business/CareersPage.vue'),
        meta: {
          title: seoMetaData.meta.businessCareers.title,
          description: seoMetaData.meta.businessCareers.description,
          mode: 'business',
        },
      },
      {
        path: 'integrations',
        name: 'layby-business-integrations',
        component: () => import('@/views/business/IntegrationsPage.vue'),
        meta: {
          title: seoMetaData.meta.businessIntegrations.title,
          description: seoMetaData.meta.businessIntegrations.description,
          mode: 'business',
        },
      },
      {
        path: 'plugin/shopify',
        name: 'layby-business-plugin-shopify',
        component: () => import('@/views/business/PluginPage.vue'),
        meta: {
          title: seoMetaData.meta.shopifyIntegration.title,
          description: seoMetaData.meta.shopifyIntegration.description,
          mode: 'business',
        },
      },
      {
        path: 'plugin/woocommerce',
        name: 'layby-business-plugin-woocommerce',
        component: () => import('@/views/business/PluginPage.vue'),
        meta: {
          title: seoMetaData.meta.woocommerceIntegration.title,
          description: seoMetaData.meta.woocommerceIntegration.description,
          mode: 'business',
        },
      },
      {
        path: 'plugin/magento',
        name: 'layby-business-plugin-magento',
        component: () => import('@/views/business/PluginPage.vue'),
        meta: {
          title: seoMetaData.meta.magentoIntegration.title,
          description: seoMetaData.meta.magentoIntegration.description,
          mode: 'business',
        },
      },
      {
        path: 'pos',
        name: 'layby-business-productshowcase',
        component: () => import('@/views/business/ProductShowCasePage.vue'),
        meta: {
          title: seoMetaData.meta.posIntegration.title,
          description: seoMetaData.meta.posIntegration.description,
          mode: 'business',
        },
      },
      {
        path: 'contact-us',
        name: 'business-contact-us',
        component: () => import('@/views/business/ContactUsPage.vue'),
        meta: {
          title: 'Contact Us',
          showSocials: false,
          mode: 'business',
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundPage.vue'),
    meta: {
      title: '404 Not Found',
    },
  },
];
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }
    // If there's a hash, scroll to that element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }
    // Don't scroll if switching between product variants
    const toPathWithoutProduct = to.path.replace(/\/(layby|plans|boost)/, '');
    const fromPathWithoutProduct = from.path.replace(/\/(layby|plans|boost)/, '');
    if (toPathWithoutProduct === fromPathWithoutProduct) {
      return false;
    }
    // Otherwise scroll to top
    return { top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const themeStore = useThemeStore();
  const metaProduct = to.meta.product as 'layby' | 'plans' | 'boost' | undefined;
  const metaMode = to.meta.mode as 'shopper' | 'business' | undefined;

  if (metaProduct && themeStore.current !== metaProduct) {
    themeStore.setProduct(metaProduct);
  }
  if (metaMode && themeStore.mode !== metaMode) {
    themeStore.setMode(metaMode);
  }

  next();
});

router.afterEach((to) => {
  const title = (to.meta.title as string) || 'LayUp Payment Technologies - LayBy Payment Solutions';
  const description =
    (to.meta.description as string) ||
    'LayUp is free to use with no interest charged. Simple, smart payments changing the way Africa transacts.';
  const image = (to.meta.image as string) || Logo;

  useSeoMeta({
    title: title,
    description: description,
    ogTitle: title,
    ogDescription: description,
    ogImage: `${BASE_URL}${image}`,
    ogType: 'website',
    ogLocale: 'en_ZA',
    ogUrl: `${BASE_URL}${to.fullPath}`,
    ogSiteName: 'LayUp Technologies',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: `${BASE_URL}${image}`,
    twitterSite: '@layup_payments',
  });
});

export default router;
